import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  DatePicker,
  Upload,
  message,
  Image,
  Tooltip,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { fetchFamilyMemberType } from "../familyMemeberType/FetchFamilyMemberType";
import { fetchHealthConditions } from "../healthCondition/FetchHealthCondition";
import moment from "moment";
import { fetchLanguages } from "../language/FetchLanguages";
import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { token } from "../../constants/Constants";
import { admin_create_family_member } from "../../endpoints/API";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const FamilyMember = ({ handleFamilyMemberModalCancel, isCustomerDetail }) => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const { Option } = Select;
  const [isMemberTypeSelected, setIsMemberTypeSelected] = useState(false);
  const [isHealthConditions, setIsHealthCondition] = useState([]);
  const [isLanguage, setIsLanguage] = useState([]);
  const [isFamilyMemberType, setIsFamilyMemberType] = useState([]);
  const [form] = Form.useForm(); // Initialize form instance for controlling the form
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const [isSelectedMemberType, setIsSelectedMemberType] = useState("");
  const isUser = useSelector((state) => state.user.user);

  const handleMemberTypeChange = (value) => {
    setIsMemberTypeSelected(!!value);

    const selectedMemberType = isFamilyMemberType.find(
      (type) => type.id === value
    );

    setIsSelectedMemberType(selectedMemberType?.name);
  };

  // Calculate age based on the date of birth
  const handleDateOfBirthChange = (date) => {
    if (date) {
      const currentYear = new Date().getFullYear();
      const age = currentYear - date.$y;
      form.setFieldsValue({ age }); // Set the calculated age in the form
    } else {
      form.setFieldsValue({ age: "" }); // Clear age if no date is selected
    }
  };
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files.");
    }
    return isImage;
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);
  const uploadButton = (
    <button
      style={{
        border: 0,
        background: "none",
      }}
      type="button"
    >
      <PlusOutlined />
      <div
        style={{
          marginTop: 8,
        }}
      >
        Upload
      </div>
    </button>
  );

  useEffect(() => {
    const getGlobalData = async () => {
      try {
        await fetchFamilyMemberType(
          setIsFamilyMemberType,
          baseUrl,
          prodTestUrl
        );
        await fetchHealthConditions(setIsHealthCondition, baseUrl, prodTestUrl);
        await fetchLanguages(setIsLanguage, baseUrl, prodTestUrl);
      } catch (error) {
        message.error("Failed to fetch details.");
      }
    };

    getGlobalData();
  }, []);

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    try {
      const base64 = await getBase64(file); // Convert to Base64 if needed
      // You can store `base64` in your state, or process it as needed
      onSuccess("ok");
    } catch (error) {
      onError(error);
    }
  };

  const handleOnFinish = async (values) => {
    try {
      // Convert each file in fileList to base64
      const base64Files = await Promise.all(
        fileList
          .filter((file) => file.originFileObj) // Ensure we're only processing actual files
          .map((file) => getBase64(file.originFileObj))
      );

      // Combine form values and base64 file(s) into the payload
      const payload = {
        ...values,
        profile_pic: base64Files[0], // Assuming only one file for profile_pic
        customer_id: isCustomerDetail.id,
        created_by: isUser.user_id,
      };

      // Send POST request to the specified URL using fetch
      const response = await admin_create_family_member(payload);
      
      if (!response.statusText=="OK") {
        const errorData =  response?.data; // Parse the error message from the response
        throw new Error(
          errorData.error || `HTTP error! Status: ${response.status}`
        );
      }

      const result = response?.data;

      message.success("Family member created successfully.");
      handleFamilyMemberModalCancel();
    } catch (error) {
      message.error(error?.response?.data?.error);
    }
  };

  useEffect(() => {
    if (isSelectedMemberType === "self" && isCustomerDetail?.first_name) {
      form.setFieldsValue({ first_name: isCustomerDetail.first_name });
      form.setFieldsValue({ middle_name: isCustomerDetail.middle_name });
      form.setFieldsValue({ last_name: isCustomerDetail.last_name });
      form.setFieldsValue({ phone: isCustomerDetail.phone });
    } else {
      form.setFieldsValue({ first_name: "" });
      form.setFieldsValue({ middle_name: "" });
      form.setFieldsValue({ last_name: "" });
      form.setFieldsValue({ phone: "" });
    }
  }, [isSelectedMemberType, isCustomerDetail?.first_name, form]);

  return (
    <div>
      <Form form={form} layout="vertical" onFinish={handleOnFinish}>
        {/* Member Type */}
        <Form.Item
          label="Member Type"
          name="member_type"
          rules={[{ required: true, message: "Please select member type" }]}
        >
          <Select
            placeholder="Select member type"
            onChange={handleMemberTypeChange}
          >
            {isFamilyMemberType?.map((type) => (
              <Select.Option key={type.id} value={type.id}>
                {type.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Profile Picture */}
        <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
          <Form.Item
            label="Profile Picture"
            name="profile_pic"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <Upload
              customRequest={handleCustomRequest}
              listType="picture-circle"
              fileList={fileList}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              onPreview={handlePreview}
              maxCount={1}
              disabled={!isMemberTypeSelected}
            >
              {fileList.length >= 1 ? null : uploadButton}
            </Upload>
            {previewImage && (
              <Image
                wrapperStyle={{
                  display: "none",
                }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
          </Form.Item>
        </Tooltip>

        <Row gutter={16}>
          {/* First Name */}
          <Col span={12}>
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[{ required: true, message: "Please enter first name" }]}
              >
                <Input
                  placeholder="Enter first name"
                  disabled={!isMemberTypeSelected}
                />
              </Form.Item>
            </Tooltip>
          </Col>

          {/* Middle Name */}
          <Col span={12}>
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item label="Middle Name" name="middle_name">
                <Input
                  placeholder="Enter middle name"
                  disabled={!isMemberTypeSelected}
                />
              </Form.Item>
            </Tooltip>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Last Name */}
          <Col span={12}>
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item
                label="Last Name"
                name="last_name"
                rules={[{ required: true, message: "Please enter last name" }]}
              >
                <Input
                  placeholder="Enter last name"
                  disabled={!isMemberTypeSelected}
                />
              </Form.Item>
            </Tooltip>
          </Col>

          {/* Date of Birth */}
          <Col span={12}>
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item label="Date of Birth" name="dob">
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Select date of birth"
                  disabled={!isMemberTypeSelected}
                  onChange={handleDateOfBirthChange} // Add onChange handler
                />
              </Form.Item>
            </Tooltip>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Gender */}
          <Col span={12}>
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item
                label="Gender"
                name="gender"
                rules={[{ required: true, message: "Please select gender" }]}
              >
                <Select
                  placeholder="Select gender"
                  disabled={!isMemberTypeSelected}
                >
                  <Option value="male">Male</Option>
                  <Option value="female">Female</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Tooltip>
          </Col>

          {/* Age */}
          <Col span={12}>
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item label="Age" name="age">
                <Input
                  type="number"
                  placeholder="Enter age"
                  disabled={!isMemberTypeSelected}
                />
              </Form.Item>
            </Tooltip>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            {/* Health Condition */}
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item
                label="Health Condition"
                name="health_condition"
                rules={[
                  { required: true, message: "Please select health condition" },
                ]}
              >
                <Select
                  placeholder="Select health condition"
                  disabled={!isMemberTypeSelected}
                  mode="multiple"
                >
                  {isHealthConditions?.map((health) => (
                    <Select.Option key={health.id} value={health.id}>
                      {health.title}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Tooltip>
          </Col>

          <Col span={12}>
            {/* Phone */}
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item
                label="Phone"
                name="phone"
                rules={[
                  { required: true, message: "Please enter phone number" },
                ]}
              >
                <Input
                  placeholder="Enter phone number"
                  disabled={!isMemberTypeSelected}
                />
              </Form.Item>
            </Tooltip>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Spoken Language */}
          <Col span={12}>
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item label="Spoken Language" name="spoken_language">
                <Select
                  placeholder="Select spoken language"
                  disabled={!isMemberTypeSelected}
                  mode="multiple"
                >
                  {isLanguage?.map((lang) => (
                    <Select.Option key={lang.id} value={lang.id}>
                      {lang.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Tooltip>
          </Col>

          {/* Preferred Language */}
          <Col span={12}>
            <Tooltip title={!isMemberTypeSelected ? "Select member type" : ""}>
              <Form.Item label="Preferred Language" name="preferred_language">
                <Select
                  placeholder="Select preferred language"
                  disabled={!isMemberTypeSelected}
                >
                  {isLanguage?.map((lang) => (
                    <Select.Option key={lang.id} value={lang.id}>
                      {lang.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Tooltip>
          </Col>
        </Row>

        {/* Submit and Cancel buttons */}
        <Form.Item style={{ display: "flex", justifyContent: "center" }}>
          <Button
            type="primary"
            htmlType="submit"
            disabled={!isMemberTypeSelected}
          >
            Submit
          </Button>
          <Button
            style={{ marginLeft: "1rem" }}
            onClick={handleFamilyMemberModalCancel}
          >
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default FamilyMember;
